import React from 'react'
import Code from './code'
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs'
import styled, { createGlobalStyle } from 'styled-components'
import { giphyDarkCharcoal, giphyGreen, giphyDarkestGrey, giphyLightGrey } from '../styles/colors'

const SCodeStyle = createGlobalStyle`
    .language-python, .language-javascript, .language-bash, .language-php, .language-ruby {
        margin: 0;
        padding: 0 0 2em 2em;
        color: #ffffff;
    }
    
    #tabs span {
        top:-30px;
    }
`

const STabs = styled(Tabs)`
    font-weight: bold;
    margin-bottom: 40px;
`

const STabPanel = styled(TabPanel)`
    display: none;
    border-bottom: 1px solid ${giphyDarkCharcoal};

    &.is-selected {
        display: block;
    }
`

const STab = styled(Tab)`
    margin-right: 30px;
    padding: 0.5em 0;
    cursor: pointer;

    &.is-selected {
        border-bottom: 1px solid ${giphyGreen};
        color: ${giphyGreen};
    }

    &:focus {
        outline: none;
        border-bottom: 1px solid ${giphyGreen};
        color: ${giphyGreen};
    }
`

const STabList = styled(TabList)`
    list-style-type: none;
    padding: 0 0 0 30px;
    display: flex;
    letter-spacing: 0;
    color: ${giphyLightGrey};
    margin: 0;
    border-bottom: 1px solid ${giphyDarkCharcoal};
`

const Collect = styled.div`
    width: 100%;
    background-color: ${giphyDarkestGrey};
    font-size: 14px;
    position: relative;
`

const JSExample = (apiName: string): string => {
    switch (apiName) {
        case 'search':
            return `//javascript, jQuery

var xhr = $.get("http://api.giphy.com/v1/gifs/search?q=ryan+gosling&api_key=YOUR_API_KEY&limit=5");
xhr.done(function(data) { console.log("success got data", data); });
            `
        case 'upload':
            return `//javascript, jQuery

var xhr = $.post("http://upload.giphy.com/v1/gifs", {api_key: "YOUR_API_KEY", username: "JoeCool3000", source_image_url: "http://www.mysite.com/myfile.mp4"});
xhr.done(function(data) { console.log("success got data", data); });
`
        default:
            return ''
    }
}

const PhpExample = (apiName: string): string => {
    switch (apiName) {
        case 'search':
            return `// php
$url = "http://api.giphy.com/v1/gifs/search?q=ryan+gosling&api_key=YOUR_API_KEY&limit=5";
print_r(json_decode(file_get_contents($url)));
`
        case 'upload':
            return `// php
$data = array(
    'api_key' => 'YOUR_API_KEY',
    'username' => 'JoeCool3000',
    'source_image_url' => 'http://www.mysite.com/myfile.mp4'
);

$url = 'http://upload.giphy.com/v1/gifs';
$ch = curl_init($url);

$postString = http_build_query($data, '', '&');

curl_setopt($ch, CURLOPT_POST, 1);
curl_setopt($ch, CURLOPT_POSTFIELDS, $postString);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

$response = curl_exec($ch);
curl_close($ch);
print_r($response);
`
        default:
            return ''
    }
}

const PythonExample = (apiName: string): string => {
    switch (apiName) {
        case 'search':
            return `# python
import urllib
import json
url = "http://api.giphy.com/v1/gifs/search"

params = urllib.parse.urlencode({
    "q": "ryan gosling",
    "api_key": "YOUR_API_KEY",
    "limit": "5"
})
with urllib.request.urlopen("".join(url, params)) as response:
    data = json.loads(response.read())

print(json.dumps(data, sort_keys=True, indent=4))
`
        case 'upload':
            return `# python
import urllib.request
import json

url = "http://upload.giphy.com/v1/gifs"

values = {
    "api_key": "YOUR_API_KEY",
    "username": "JoeCool3000",
    "source_image_url": "http://www.mysite.com/myfile.mp4"
}

headers = {
    "Content-Type": "application/json",
    "Accept": "application/json",
}

data = json.dumps(values).encode("utf-8")

req = urllib.request.Request(url, data, headers)
with urllib.request.urlopen(req) as res:
    print(res.read().decode())
`
        default:
            return ``
    }
}

const RubyExample = (apiName: string): string => {
    switch (apiName) {
        case 'search':
            return `#ruby
require 'net/http'
require 'json'
url="http://api.giphy.com/v1/gifs/search?q=ryan+gosling&api_key=YOUR_API_KEY&limit=5"
resp = Net::HTTP.get_response(URI.parse(url))
buffer = resp.body
result = JSON.parse(buffer)
puts result
`
        case 'upload':
            return `#ruby
require 'net/http'
uri = URI('http://upload.giphy.com/v1/gifs')
res = Net::HTTP.post_form(uri, 'api_key' => 'YOUR_API_KEY', 'username' => 'JoeCool3000', 'source_image_url' => 'http://www.mysite.com/myfile.mp4')
puts res.body
`
        default:
            return ''
    }
}

const ShellExample = (apiName: string): string => {
    switch (apiName) {
        case 'search':
            return `# curl, command line
curl "http://api.giphy.com/v1/gifs/search?q=ryan+gosling&api_key=YOUR_API_KEY&limit=5"
`
        case 'upload':
            return `# curl, command line 
curl --data "api_key=YOUR_API_KEY&username=JoeCool3000&source_image_url:=http://www.mysite.com/myfile.mp4" http://upload.giphy.com/v1/gifs
            `
        default:
            return ''
    }
}

const CodeSwitcher = ({ apiName }: { apiName: string }) => {
    return (
        <Collect>
            <SCodeStyle />
            <STabs selectedTabClassName="is-selected" selectedTabPanelClassName="is-selected" id="tabs">
                <STabList>
                    <STab>Python</STab>
                    <STab>Javascript</STab>
                    <STab>Ruby</STab>
                    <STab>PHP</STab>
                    <STab>Bash</STab>
                </STabList>
                <STabPanel>
                    <Code language="python" showLineNumber>
                        {PythonExample(apiName)}
                    </Code>
                </STabPanel>
                <STabPanel>
                    <Code language="javascript" showLineNumber>
                        {JSExample(apiName)}
                    </Code>
                </STabPanel>
                <STabPanel>
                    <Code language="ruby" showLineNumber>
                        {RubyExample(apiName)}
                    </Code>
                </STabPanel>
                <STabPanel>
                    <Code language="php" showLineNumber>
                        {PhpExample(apiName)}
                    </Code>
                </STabPanel>
                <STabPanel>
                    <Code language="bash" showLineNumber>
                        {ShellExample(apiName)}
                    </Code>
                </STabPanel>
            </STabs>
        </Collect>
    )
}

export default CodeSwitcher
